import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daih0sbp14a1h3mr744g";

const topics = ["内容线索", "纠错反馈", "合作咨询", "其他建议"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        "";

      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg || "提交失败，请稍后重试，或通过邮箱与我们联系。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-xl font-black text-foreground-950">
        给我们留言
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        发现线索、需要纠错，或者想聊聊合作，都可以通过下面的表单告诉我们。我们会在整理时段统一查看。
      </p>

      <form
        data-readdy-form
        id="contact-form"
        onSubmit={handleSubmit}
        className="mt-6 space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="contact-name"
              className="mb-1.5 block text-sm font-medium text-foreground-800"
            >
              称呼
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              placeholder="怎么称呼你"
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <div>
            <label
              htmlFor="contact-email"
              className="mb-1.5 block text-sm font-medium text-foreground-800"
            >
              邮箱
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              placeholder="用于接收回复"
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="contact-topic"
            className="mb-1.5 block text-sm font-medium text-foreground-800"
          >
            留言类型
          </label>
          <select
            id="contact-topic"
            name="topic"
            defaultValue={topics[0]}
            className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            {topics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="contact-message"
            className="mb-1.5 block text-sm font-medium text-foreground-800"
          >
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="简单描述一下你想反馈的内容（不超过 500 字）"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          ></textarea>
        </div>

        <input
          className="field-verify-alt"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-primary-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-send-plane-line"></i>
            {status === "submitting" ? "提交中..." : "提交留言"}
          </button>
          <p className="text-xs text-foreground-500">
            也可以直接发邮件至 contact@heiliaodaily.example
          </p>
        </div>

        {status === "success" && (
          <div className="flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
            <span>留言已提交成功，感谢你的反馈，我们会尽快查看。</span>
          </div>
        )}

        {status === "error" && (
          <div className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5 text-base"></i>
            <span>{formError}</span>
          </div>
        )}
      </form>
    </div>
  );
}